import { useCallback, useState } from 'react';
import type { FC } from 'react';

import { FormattedMessage } from 'react-intl';

import CheckIcon from '@/material-icons/400-24px/check.svg?react';
import { Avatar } from 'mastodon/components/avatar';
import { Button } from 'mastodon/components/button';
import { DisplayName } from 'mastodon/components/display_name';
import { Icon } from 'mastodon/components/icon';
import { useAccount } from 'mastodon/hooks/useAccount';
import { useIdentity } from 'mastodon/identity_context';
import { switchableAccountIds } from 'mastodon/initial_state';
import {
  addAccount,
  errorMessageFrom,
  MAX_SWITCHABLE_ACCOUNTS,
  switchAccount,
} from 'mastodon/utils/account_switcher';

import { DialogModal } from './dialog_modal';
import type { DialogModalProps } from './dialog_modal';
import classes from './switch_account_modal.module.scss';

export const AccountRow: FC<{
  accountId: string;
  current: boolean;
  disabled: boolean;
  onSelect: (accountId: string) => void;
}> = ({ accountId, current, disabled, onSelect }) => {
  const account = useAccount(accountId);

  const handleClick = useCallback(() => {
    onSelect(accountId);
  }, [accountId, onSelect]);

  return (
    <li>
      <button
        type='button'
        className={classes.account}
        onClick={handleClick}
        disabled={disabled || current}
        aria-current={current ? 'true' : undefined}
      >
        <Avatar account={account} size={40} />
        <DisplayName account={account} className={classes.name} />
        {current && (
          <Icon id='check' icon={CheckIcon} className={classes.check} />
        )}
      </button>
    </li>
  );
};

/**
 * The accounts one can switch to and what to do with them, shared by this
 * modal and the sidebar's bubble (SwitchAccountBubble)
 */
export const useAccountSwitcher = () => {
  const { accountId } = useIdentity();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const accountIds = accountId
    ? [accountId, ...switchableAccountIds]
    : switchableAccountIds;

  const canAddAccount = accountIds.length < MAX_SWITCHABLE_ACCOUNTS;

  const handleFailure = useCallback((e: unknown) => {
    setPending(false);
    setError(errorMessageFrom(e) ?? 'error');
  }, []);

  const handleSelect = useCallback(
    (targetId: string) => {
      setPending(true);
      setError(undefined);
      switchAccount(targetId).catch(handleFailure);
    },
    [handleFailure],
  );

  const handleAdd = useCallback(() => {
    setPending(true);
    setError(undefined);
    addAccount().catch(handleFailure);
  }, [handleFailure]);

  return {
    accountId,
    accountIds,
    canAddAccount,
    pending,
    error,
    handleSelect,
    handleAdd,
  };
};

/** The limit note and an error, under the list of accounts */
export const SwitchAccountNotes: FC<{
  canAddAccount: boolean;
  error: string | undefined;
}> = ({ canAddAccount, error }) => (
  <>
    {!canAddAccount && (
      <p className={classes.hint}>
        <FormattedMessage
          id='account_switcher.limit_reached'
          defaultMessage='You can be signed in to at most {count} accounts at once.'
          values={{ count: MAX_SWITCHABLE_ACCOUNTS }}
        />
      </p>
    )}

    {error && (
      <p className={classes.error} role='alert'>
        {error === 'error' ? (
          <FormattedMessage
            id='account_switcher.error'
            defaultMessage='Something went wrong. Please try again.'
          />
        ) : (
          error
        )}
      </p>
    )}
  </>
);

export const SwitchAccountModal: FC<DialogModalProps> = ({ onClose }) => {
  const {
    accountId,
    accountIds,
    canAddAccount,
    pending,
    error,
    handleSelect,
    handleAdd,
  } = useAccountSwitcher();

  return (
    <DialogModal
      onClose={onClose}
      noCancelButton
      title={
        <FormattedMessage
          id='account_switcher.title'
          defaultMessage='Switch account'
        />
      }
      buttons={
        canAddAccount ? (
          <Button secondary onClick={handleAdd} disabled={pending}>
            <FormattedMessage
              id='account_switcher.add_account'
              defaultMessage='Add an existing account'
            />
          </Button>
        ) : undefined
      }
    >
      <ul className={classes.list}>
        {accountIds.map((id) => (
          <AccountRow
            key={id}
            accountId={id}
            current={id === accountId}
            disabled={pending}
            onSelect={handleSelect}
          />
        ))}
      </ul>

      <SwitchAccountNotes canAddAccount={canAddAccount} error={error} />
    </DialogModal>
  );
};
